import { collectCustomFields, getParamOrHidden } from './utils.js';
import { setLoading, showFeedback, showErrorBox, showToast } from './form-helpers.js';
import {
  createCheckoutForGuest,
  registerGuestForTicket,
} from './tickets.api.js';
import { revealWaitlistFormForTicket, showWaitlistConfirmation } from './waitlist-management.js';
import {
  getBotProtectionConfig,
  injectBotProtection,
  resolveBotProtectionResponse,
  refreshBotProtection,
} from './bot-protection.js';
import { extractErrorMessage } from './error-utils.js';
import { safeRedirect } from '../checkout-core/security.js';
import { initLiveValidation, generateCustomFieldRules, validateCustomFields } from '../checkout-core/validation.js';
import { API_BASE, apiCall } from '../checkout-core/api.js';
import { collectCheckoutPayload, validateCheckoutState, resetCheckoutState } from './checkout-enhancements.js';

const __ = (typeof window.__ === 'function')
  ? window.__
  : function(message, replacements) {
    const text = String(message == null ? '' : message);
    if (!Array.isArray(replacements) || replacements.length === 0) return text;
    return text.replace(/\{(\d+)\}/g, (match, idx) => {
      const value = replacements[Number(idx)];
      return value === undefined || value === null ? match : String(value);
    });
  };

function validateAge(fd, minAge, ageVerificationMode = 'birth_date') {
  if (!minAge || minAge <= 0) return { valid: true };

  // Checkbox mode: only check age_confirmed
  if (ageVerificationMode === 'checkbox') {
    const ageConfirmed = fd.get('age_confirmed');
    if (!ageConfirmed) {
      return { valid: false, message: __('Bitte bestätige dein Alter.') };
    }
    return { valid: true };
  }

  // Birth date mode: validate actual birth date
  const raw = fd.get('birth_date');
  const birth = raw ? new Date(raw) : null;
  if (!birth || Number.isNaN(birth.getTime())) {
    return { valid: false, message: __('Bitte gib ein gültiges Geburtsdatum ein.') };
  }
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const month = today.getMonth() - birth.getMonth();
  if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  if (age < minAge) {
    return {
      valid: false,
      message: __('Du musst mindestens {0} Jahre alt sein, um dieses Ticket zu buchen.', [minAge]),
    };
  }
  return { valid: true };
}

function showConsentError(box, hasOrganizerTerms) {
  const msg = hasOrganizerTerms
    ? __('Bitte akzeptiere DSGVO, AGB und die Veranstalter-AGBs.')
    : __('Bitte akzeptiere DSGVO, AGB.');
  showFeedback(box, msg, 'error');
  showToast(msg, 'error');
}

function ensureCashNotice(message) {
  const msg = (message || '').trim();
  if (!msg) {
    return __('Registrierung erfolgreich! Bitte bezahle vor Ort.');
  }
  const lower = msg.toLowerCase();
  if (lower.includes('bar') || lower.includes('vor ort') || lower.includes('vor-ort') || lower.includes('cash')) {
    return msg;
  }
  return `${msg} ${__('Bitte bezahle vor Ort.')}`;
}

/**
 * WELLE 2 (P2-1): Build the tokenless /checkout/success fallback URL so every
 * successful guest registration terminal converges on the success page even when
 * the backend could not mint a checkout token (ticket_url missing). The state
 * drives the generic, PII-free banner; safeRedirect (http/redirect.js) permits
 * this relative same-origin path.
 * @param {{requiresApproval?: boolean, isCash?: boolean}} [opts]
 * @returns {string}
 */
function buildCheckoutSuccessFallbackUrl({ requiresApproval = false, isCash = false } = {}) {
  let state = 'confirmed';
  if (requiresApproval) state = 'pending_approval';
  else if (isCash) state = 'pay_on_site';
  return '/checkout/success?state=' + state;
}

export function initGuestRegistration(root, config, { attachGuestGate, openInlineLogin } = {}) {
  if (!root) {
    return;
  }

  const forms = Array.from(root.querySelectorAll('.register-guest-form'));
  if (!forms.length) {
    return;
  }

  if (typeof attachGuestGate === 'function') {
    attachGuestGate(forms);
  }

  forms.forEach((form) => {
    if (form.dataset.guestSubmitInit === '1') {
      return;
    }
    form.dataset.guestSubmitInit = '1';

    // Initialize live validation (WCAG 3.3.1/3.3.3 compliance)
    // Support both data-min-age (modern templates) and data-minage (legacy)
    const minAge = parseInt(form.dataset.minAge || form.dataset.minage || '0', 10);
    const ageVerificationMode = form.dataset.ageMode || form.dataset.agemode || form.dataset.ageverificationmode || 'birth_date';
    initLiveValidation(form, { minAge, ageVerificationMode });

    // Bot protection is deferred to form expansion (openInlineCheckout)
    // to avoid N parallel API calls + DOM injections for hidden forms.
    // The submit handler (below) has its own getBotProtectionConfig fallback.

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      // Double-submission guard
      if (form.dataset.submitting === '1') return;
      form.dataset.submitting = '1';
      let keepSubmitGuard = false;

      const submitBtn = form.querySelector('button[type=submit]');
      if (submitBtn && !submitBtn.dataset.originalText) {
        submitBtn.dataset.originalText = submitBtn.textContent;
      }

      if (form.dataset.inlineGatePending === '1' && form.dataset.inlineBypass !== '1') {
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      if (form.dataset.inlineLoginActive === '1' && form.dataset.inlineBypass !== '1') {
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const fd = new FormData(form);
      const ticket = form.dataset.ticket;
      // Support both data-min-age (modern templates) and data-minage (legacy)
      const minAge = parseInt(form.dataset.minAge || form.dataset.minage || '0', 10);
      const isPaid = String(form.dataset.ispaid || '0') === '1';
      const isFree = String(form.dataset.isfree || '0') === '1';
      const isCash = !isPaid && !isFree;
      const requiresApproval = String(form.dataset.requiresApproval || '0') === '1' || form.dataset.requiresApproval === 'true';
      // Support both data-age-mode (kebab) and data-ageverificationmode
      const ageVerificationMode = form.dataset.ageMode || form.dataset.agemode || form.dataset.ageverificationmode || 'birth_date';
      const feedbackBox =
        form.querySelector('.feedback-box') || form.querySelector('.feedback') || form;

      feedbackBox.className = 'feedback-box hidden mt-2';
      feedbackBox.innerHTML = '';

      setLoading(submitBtn, true);

      // Outer error boundary: ensures spinner is always cleared even if
      // pre-try validation code (collectCheckoutPayload, getParamOrHidden) throws
      try {

      const ageCheck = validateAge(fd, minAge, ageVerificationMode);
      if (!ageCheck.valid) {
        showFeedback(feedbackBox, ageCheck.message, 'error');
        showToast(ageCheck.message, 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const hasOrganizerTerms = form.querySelector('input[name="has_organizer_terms"]')?.value === '1';
      const consentDsgvo = !!fd.get('consent_dsgvo');
      const consentAgb = !!fd.get('consent_agb');
      const consentOrganizer = hasOrganizerTerms ? !!fd.get('consent_organizer_terms') : true;
      const consentMarketing = !!fd.get('consent_marketing');
      const consentEventUpdates = !!fd.get('consent_event_updates');

      if (!consentDsgvo || !consentAgb || !consentOrganizer) {
        showConsentError(feedbackBox, hasOrganizerTerms);
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const checkoutValidation = validateCheckoutState(form);
      if (!checkoutValidation.valid) {
        const msg = checkoutValidation.message || __('Bitte prüfe die Angaben für Gruppen/Begleitpersonen.');
        showFeedback(feedbackBox, msg, 'error');
        showToast(msg, 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const { payload: checkoutPayload, plusOnes } = collectCheckoutPayload(form);

      const ticketcreativeToken = getParamOrHidden(form, 'ticketcreative_token');
      const inviteToken = getParamOrHidden(form, ['invite', 'invite_token']);
      const inviteCode = getParamOrHidden(form, 'invite_code');
      const accessCode = getParamOrHidden(form, 'access_code');

      let botConfig = null;
      let botResponse = null;
      try {
        botConfig = await getBotProtectionConfig(config?.eventName, { inviteToken, inviteCode });
        if (botConfig?.error_code) {
          const msg = botConfig.message || __('Bot-Schutz ist fehlerhaft konfiguriert.');
          showFeedback(feedbackBox, msg, 'error');
          showToast(msg, 'error');
          setLoading(submitBtn, false);
          form.dataset.submitting = '0';
          return;
        }
        if (botConfig?.enabled && botConfig.mode !== 'none') {
          await injectBotProtection(form, botConfig);
          botResponse = await resolveBotProtectionResponse(botConfig, form);
          if (!botResponse) {
            showFeedback(feedbackBox, __('Bitte die Bot-Verifizierung abschließen.'), 'error');
            showToast(__('Bitte die Bot-Verifizierung abschließen.'), 'error');
            setLoading(submitBtn, false);
            form.dataset.submitting = '0';
            return;
          }
        }
      } catch (error) {
        showFeedback(feedbackBox, __('Bot-Verifizierung konnte nicht geladen werden.'), 'error');
        showToast(__('Bot-Verifizierung konnte nicht geladen werden.'), 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }

      const membershipRequired = form.dataset.membershipRequired === '1' || form.dataset.membershipRequired === 'true';
      const hasMembership = !!fd.get('has_membership_number');
      const membershipNumber = membershipRequired
        ? (fd.get('membership_number') || '').trim()
        : hasMembership
        ? (fd.get('membership_number') || '').trim()
        : '';
      if (membershipRequired && !membershipNumber) {
        showFeedback(feedbackBox, __('Bitte gib deine Zusatzangabe an.'), 'error');
        showToast(__('Bitte gib deine Zusatzangabe an.'), 'error');
        setLoading(submitBtn, false);
        form.dataset.submitting = '0';
        return;
      }
      // Client-side custom field validation
      const customFieldDefs = form.dataset.customFieldDefs
        ? JSON.parse(form.dataset.customFieldDefs)
        : [];
      if (customFieldDefs.length > 0) {
        const customRules = generateCustomFieldRules(customFieldDefs);
        if (!validateCustomFields(form, customRules)) {
          setLoading(submitBtn, false);
          form.dataset.submitting = '0';
          return;
        }
      }

      const customFieldsPayload = collectCustomFields(form);
      const customFieldsJson = customFieldsPayload ? JSON.stringify(customFieldsPayload) : null;

      try {
        if (isPaid) {
          const policy = document.getElementById('event_account_policy')?.value || 'optional';
          const defaultCreate = (document.getElementById('event_account_policy_default')?.value || '1') === '1';
          const cb = form.querySelector('input[name="provision_account"]');
          const provisionAccount = cb
            ? cb.checked
            : policy === 'required'
            ? true
            : policy === 'optional'
            ? defaultCreate
            : false;

          if (requiresApproval) {
            const approvalResponse = await apiCall(`${API_BASE}.payments.stripe_checkout.request_approval_for_ticket`, {
              first_name: fd.get('first_name'),
              last_name: fd.get('last_name'),
              email: fd.get('email'),
              phone: fd.get('phone') || '',
              birth_date: ageVerificationMode === 'birth_date' ? fd.get('birth_date') : null,
              age_confirmed: ageVerificationMode === 'checkbox' ? !!fd.get('age_confirmed') : false,
              event: config?.eventName,
              ticket_type: ticket,
              consent_dsgvo: true,
              consent_agb: true,
              consent_organizer_terms: consentOrganizer,
              consent_marketing: consentMarketing,
              consent_event_updates: consentEventUpdates,
              invite_token: inviteToken,
              invite_code: inviteCode,
              access_code: accessCode || undefined,
              membership_number: membershipNumber,
              provision_account: provisionAccount,
              user_agent: navigator.userAgent || '',
              ticketcreative_token: ticketcreativeToken,
              bot_challenge_response: botResponse || undefined,
              ...(plusOnes.length > 0 ? { plus_ones: JSON.stringify(plusOnes) } : {}),
              ...checkoutPayload,
              ...(customFieldsJson ? { custom_fields: customFieldsJson } : {}),
            });

            if (approvalResponse?.success) {
              const approvalData = approvalResponse?.data || {};
              const baseMsg =
                approvalResponse?.toast ||
                __('Anfrage gesendet! Du erhältst eine E-Mail, sobald deine Anfrage bearbeitet wurde.');
              const approvalFallback = `/event/${config?.eventSlug || ''}${accessCode ? '?code=' + encodeURIComponent(accessCode) : ''}`;
              const isWaitlist = approvalData?.waitlisted === true;

              if (!isWaitlist) {
                // WELLE 2 (P2-1): converge on /checkout/success. Backend returns a
                // ticket_url for approval-pending; the tokenless fallback prevents
                // the previous form-collapse strand on token-generation failure.
                const successUrl =
                  approvalData?.ticket_url ||
                  buildCheckoutSuccessFallbackUrl({ requiresApproval: true });
                showFeedback(feedbackBox, baseMsg, 'success');
                showToast(baseMsg, 'success');
                form.reset();
                resetCheckoutState(form);
                setTimeout(() => {
                  safeRedirect(successUrl, approvalFallback);
                }, 600);
                return;
              }

              // Waitlist (OD-1, Option B): persistente Inline-Bestätigungs-Karte
              // statt Toast+Collapse.
              showToast(baseMsg, 'success');
              form.reset();
              resetCheckoutState(form);
              showWaitlistConfirmation(form, {
                position: approvalData?.waitlist?.position,
                total: approvalData?.waitlist?.total,
              });
              return;
            }

            const approvalError = extractErrorMessage(approvalResponse, __('Anfrage konnte nicht gesendet werden.'));
            showFeedback(feedbackBox, approvalError, 'error');
            showToast(approvalError, 'error');
            if (botConfig?.enabled && botConfig.mode === 'simple') {
              await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
            }
            return;
          }

          const eventFallback = `/event/${config?.eventSlug || ''}${accessCode ? '?code=' + encodeURIComponent(accessCode) : ''}`;
          const response = await createCheckoutForGuest({
            first_name: fd.get('first_name'),
            last_name: fd.get('last_name'),
            email: fd.get('email'),
            phone: fd.get('phone') || '',
            birth_date: ageVerificationMode === 'birth_date' ? fd.get('birth_date') : null,
            age_confirmed: ageVerificationMode === 'checkbox' ? !!fd.get('age_confirmed') : false,
            event: config?.eventName,
            ticket_type: ticket,
            consent_dsgvo: true,
            consent_agb: true,
            consent_organizer_terms: consentOrganizer,
            consent_marketing: consentMarketing,
            consent_event_updates: consentEventUpdates,
            provision_account: provisionAccount,
            user_agent: navigator.userAgent || '',
            ticketcreative_token: ticketcreativeToken,
            invite_token: inviteToken,
            invite_code: inviteCode,
            access_code: accessCode || undefined,
            membership_number: membershipNumber,
            bot_challenge_response: botResponse || undefined,
            ...(plusOnes.length > 0 ? { plus_ones: JSON.stringify(plusOnes) } : {}),
            ...checkoutPayload,
            ...(customFieldsJson ? { custom_fields: customFieldsJson } : {}),
          });

          if (response?.success) {
            const data = response?.data || {};

            if (data?.free_checkout) {
              const isIdempotent = !!data?.idempotent;
              const toastType = isIdempotent ? 'info' : 'success';
              const successMsg = response?.toast || __('Erfolgreich registriert!');
              // F3/R1/R2: If account provisioning was downgraded, show info in feedbackBox
              // and success only as toast (prevents overwrite). Generic message only (no enumeration leak).
              const hasDowngrade = data?.account_provisioned === false && data?.provision_downgraded;
              if (hasDowngrade) {
                showFeedback(feedbackBox, __('Dein Konto konnte nicht erstellt werden. Du kannst es später in deinem Profil nachholen.'), 'info');
                showToast(successMsg, toastType);
              } else {
                showFeedback(feedbackBox, successMsg, toastType);
                showToast(successMsg, toastType);
              }
              // WELLE 2 (P2-1): free-via-promo converges on /checkout/success
              // instead of falling back to the event page.
              const redirectUrl = data?.ticket_url || buildCheckoutSuccessFallbackUrl();
              setTimeout(() => {
                safeRedirect(redirectUrl, eventFallback);
              }, 600);
              return;
            }

            if (data?.checkout_url) {
              // FE-005: Use safeRedirect to prevent Open Redirect (CWE-601)
              if (submitBtn) submitBtn.classList.add('is-redirecting');
              safeRedirect(data.checkout_url, eventFallback);
              return;
            }
          }

          // CC02-002: Handle account_exists error from server-side check.
          // The email_has_account endpoint no longer reveals account existence;
          // instead the checkout/registration endpoint returns this error code
          // so we can prompt the user to log in at this stage.
          if (['account_exists', 'account_check', 'account_required'].includes(response?.code) && typeof openInlineLogin === 'function') {
            showToast(
              __('Diese E-Mail ist bereits registriert. Bitte kurz einloggen – danach geht\'s automatisch weiter.'),
              'warning',
            );
            form.dataset.inlineLoginActive = '1';
            openInlineLogin(form, () => {
              if (form.dataset.inlineLoginActive === '1') {
                delete form.dataset.inlineLoginActive;
              }
              // Guest bypass: disable account provisioning to prevent account_check loop
              const provCb = form.querySelector('input[name="provision_account"]');
              if (provCb) provCb.checked = false;
              form.dataset.inlineBypass = '1';
              if (form.dataset.inlineBypass === '1') {
                form.requestSubmit();
              }
            });
            return;
          }

          // A-2 / H-13: paid, sold-out ticket with waitlist enabled — the server returns
          // SOLD_OUT_WAITLIST_AVAILABLE (data.redirect="waitlist"). Route the guest to the
          // waitlist join instead of dead-ending on a generic Stripe payment error.
          if (response?.code === 'sold_out_waitlist_available' || response?.data?.redirect === 'waitlist') {
            const waitlistMsg = extractErrorMessage(
              response,
              __('Dieses Ticket ist ausverkauft. Du kannst dich auf die Warteliste setzen.'),
            );
            showFeedback(feedbackBox, waitlistMsg, 'info');
            showToast(waitlistMsg, 'info');
            if (!revealWaitlistFormForTicket(ticket)) {
              // Race-sellout: the ticket was available at page-load, so the server-rendered
              // waitlist join button is not in the DOM. Reload the tickets page so the server
              // re-renders the sold-out state (which exposes the waitlist join button).
              setTimeout(() => { try { window.location.reload(); } catch (_) { /* noop */ } }, 1500);
            }
            return;
          }

          const msg = extractErrorMessage(response, __('Zahlung konnte nicht gestartet werden (Stripe).'));
          showFeedback(feedbackBox, msg, 'error');
          showToast(msg, 'error');
          if (botConfig?.enabled && botConfig.mode === 'simple') {
            await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
          }
          return;
        }

        const policy = document.getElementById('event_account_policy')?.value || 'optional';
        const defaultCreate = (document.getElementById('event_account_policy_default')?.value || '1') === '1';
        const cb = form.querySelector('input[name="provision_account"]');
        const provisionAccount = cb
          ? cb.checked
          : policy === 'required'
          ? true
          : policy === 'optional'
          ? defaultCreate
          : false;

        const args = {
          first_name: fd.get('first_name'),
          last_name: fd.get('last_name'),
          email: fd.get('email'),
          phone: fd.get('phone') || '',
          birth_date: ageVerificationMode === 'birth_date' ? fd.get('birth_date') : null,
          age_confirmed: ageVerificationMode === 'checkbox' ? !!fd.get('age_confirmed') : false,
          event: config?.eventName,
          ticket_type: ticket,
          consent_dsgvo: true,
          consent_agb: true,
          consent_organizer_terms: consentOrganizer,
          consent_marketing: consentMarketing,
          consent_event_updates: consentEventUpdates,
          ticketcreative_token: ticketcreativeToken,
          provision_account: provisionAccount,
          membership_number: membershipNumber,
          user_agent: navigator.userAgent || '',
          bot_challenge_response: botResponse || undefined,
          ...(plusOnes.length > 0 ? { plus_ones: JSON.stringify(plusOnes) } : {}),
          access_code: accessCode || undefined,
          ...checkoutPayload,
        };

        if (customFieldsJson) {
          args.custom_fields = customFieldsJson;
        }
        if (inviteToken) {
          args.invite_token = inviteToken;
        }
        if (inviteCode) {
          args.invite_code = inviteCode;
        }

        const response = await registerGuestForTicket(args);

        if (response?.success) {
          const data = response?.data || {};

          // F3 fix: Track if account provisioning was downgraded (R1/R2: generic message, no enumeration leak)
          const hasDowngrade = data?.account_provisioned === false && data?.provision_downgraded;
          const isWaitlist = data?.status === 'Warteliste';

          if (!isWaitlist) {
            // WELLE 2 (P2-1): every non-waitlist success converges on the single
            // /checkout/success terminal (confirmed / approval-pending / pay-on-site).
            // ticket_url is backend-provided; the tokenless fallback prevents the
            // previous form-collapse strand on token-generation failure.
            const isIdempotent = !!data?.idempotent;
            const toastType = isIdempotent ? 'info' : 'success';
            const baseMsg =
              response?.toast ||
              (requiresApproval
                ? __('Anfrage gesendet! Du erhältst eine E-Mail, sobald deine Anfrage bearbeitet wurde.')
                : isCash
                ? __('Registrierung erfolgreich! Bitte bezahle vor Ort.')
                : __('Registrierung erfolgreich!'));
            const successMsg = isCash && !requiresApproval ? ensureCashNotice(baseMsg) : baseMsg;
            if (hasDowngrade) {
              // R1 fix: Show downgrade in feedbackBox, success only as toast (no overwrite)
              showFeedback(feedbackBox, __('Dein Konto konnte nicht erstellt werden. Du kannst es später in deinem Profil nachholen.'), 'info');
              showToast(successMsg, toastType);
            } else {
              showFeedback(feedbackBox, successMsg, toastType);
              showToast(successMsg, toastType);
            }
            form.reset();
            resetCheckoutState(form);
            const successUrl =
              data?.ticket_url || buildCheckoutSuccessFallbackUrl({ requiresApproval, isCash });
            setTimeout(() => {
              safeRedirect(successUrl, `/event/${config?.eventSlug || ''}`);
            }, 600);
            return;
          }

          // Waitlist (OD-1, Option B): persistente Inline-Bestätigungs-Karte statt
          // Toast+Collapse (vorher fluechtig — Bestaetigung verschwand mit dem Form).
          const waitMsg =
            response?.toast ||
            __('Du stehst auf der Warteliste. Wir benachrichtigen dich, sobald ein Platz frei wird.');
          showToast(waitMsg, 'success');
          if (hasDowngrade) {
            showToast(__('Dein Konto konnte nicht erstellt werden. Du kannst es später in deinem Profil nachholen.'), 'info');
          }
          form.reset();
          resetCheckoutState(form);
          showWaitlistConfirmation(form, {
            position: data?.waitlist?.position,
            total: data?.waitlist?.total,
          });
        } else {
          // Server-side error (503/502/504): registration may have been created despite error response
          const SERVER_ERROR_CODES = ['service_unavailable', 'bad_gateway', 'gateway_timeout', 'fetch_failed'];
          if (SERVER_ERROR_CODES.includes(response?.code)) {
            keepSubmitGuard = true;
            const warnMsg = __('Server antwortet nicht. Deine Registrierung wurde möglicherweise bereits erstellt. Bitte prüfe deine E-Mail.');
            showFeedback(feedbackBox, warnMsg, 'warning');
            showToast(warnMsg, 'warning');
            if (submitBtn) {
              submitBtn.disabled = true;
              submitBtn.textContent = __('Bitte E-Mail prüfen');
            }
            // Allow retry after 30s
            setTimeout(() => {
              form.dataset.submitting = '0';
              if (submitBtn) {
                submitBtn.disabled = false;
                submitBtn.textContent = submitBtn.dataset.originalText || __('Registrieren');
              }
            }, 30000);
          // CC02-002: Handle account_exists error for free registrations too
          } else if (['account_exists', 'account_check', 'account_required'].includes(response?.code) && typeof openInlineLogin === 'function') {
            showToast(
              __('Diese E-Mail ist bereits registriert. Bitte kurz einloggen – danach geht\'s automatisch weiter.'),
              'warning',
            );
            form.dataset.inlineLoginActive = '1';
            openInlineLogin(form, () => {
              if (form.dataset.inlineLoginActive === '1') {
                delete form.dataset.inlineLoginActive;
              }
              // Guest bypass: disable account provisioning to prevent account_check loop
              const provCb = form.querySelector('input[name="provision_account"]');
              if (provCb) provCb.checked = false;
              form.dataset.inlineBypass = '1';
              if (form.dataset.inlineBypass === '1') {
                form.requestSubmit();
              }
            });
          } else {
            const msg = extractErrorMessage(response, __('Registrierung fehlgeschlagen.'));
            showFeedback(feedbackBox, msg, 'error');
            showToast(msg, 'error');
            if (botConfig?.enabled && botConfig.mode === 'simple') {
              await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
            }
          }
        }
      } catch (error) {
        const fallbackMsg = isPaid
          ? __('Zahlung konnte nicht gestartet werden (Stripe).')
          : __('Fehler beim Registrieren. Bitte später erneut versuchen.');
        const msg = extractErrorMessage(error, fallbackMsg);
        showFeedback(feedbackBox, msg, 'error');
        showToast(msg, 'error');
        if (botConfig?.enabled && botConfig.mode === 'simple') {
          await refreshBotProtection(form, config?.eventName, { inviteToken, inviteCode });
        }
      } finally {
        setLoading(submitBtn, false);
        if (!keepSubmitGuard) {
          form.dataset.submitting = '0';
        }
      }

      } catch (outerError) {
        // Catches unexpected errors from validation/DOM code before the inner try
        const msg = __('Fehler beim Registrieren. Bitte später erneut versuchen.');
        showFeedback(feedbackBox, msg, 'error');
        showToast(msg, 'error');
      } finally {
        setLoading(submitBtn, false);
        if (!keepSubmitGuard) {
          form.dataset.submitting = '0';
        }
      }
    });
  });
}
